import React, { useState } from 'react';
import Icon from './Icon';
import { FabricAssignment, Fabric } from '../types/ui/Asoebi';
import { useMoney } from '../hooks/useMoney';
import '../styles/components/PaymentModal.css';

interface PaymentModalProps {
  assignment: FabricAssignment;
  fabric?: Fabric;
  /** The item name to show — a bundle order has no fabric row, so the caller resolves it. */
  itemLabel: string;
  onPaymentComplete: (assignmentId: string, amount: string, method?: string, reference?: string) => Promise<void> | void;
  onClose: () => void;
}

/** Records one payment against an asoebi order — a decimal string, never a float. */
const PaymentModal: React.FC<PaymentModalProps> = ({ assignment, itemLabel, onPaymentComplete, onClose }) => {
  const { format, symbol } = useMoney();
  const already = assignment.payment_amount ?? '0';
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('');
  const [reference, setReference] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isValidAmount = /^\d+(\.\d{1,2})?$/.test(amount.trim()) && Number(amount) > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidAmount) { setError('Enter an amount greater than zero, with at most 2 decimals.'); return; }
    setSaving(true);
    setError(null);
    try {
      await onPaymentComplete(assignment.id, amount.trim(), method.trim() || undefined, reference.trim() || undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record payment.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pm-container">
      <div className="pm-header">
        <h2>Record payment</h2>
        <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
          <Icon name="x" size={18} />
        </button>
      </div>
      <p className="pm-item-label">{itemLabel} — {assignment.guest_name ?? 'Guest'}</p>
      <p className="pm-already-paid">Already recorded: {assignment.currency ? format(Number(already)) : already}</p>
      {error && <div className="pm-error" role="alert">{error}</div>}
      <form onSubmit={handleSubmit} className="pm-form">
        <div className="form-group">
          <label htmlFor="pm-amount">Amount{symbol ? ` (${symbol})` : ''}</label>
          <input id="pm-amount" type="text" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" autoFocus />
        </div>
        <div className="form-group">
          <label htmlFor="pm-method">Method</label>
          <input id="pm-method" type="text" value={method} onChange={e => setMethod(e.target.value)} placeholder="bank-transfer, cash…" />
        </div>
        <div className="form-group">
          <label htmlFor="pm-reference">Reference</label>
          <input id="pm-reference" type="text" value={reference} onChange={e => setReference(e.target.value)} placeholder="optional" />
        </div>
        <div className="pm-actions">
          <button type="button" className="secondary-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="primary-btn" disabled={saving || !isValidAmount}>{saving ? 'Saving…' : 'Record payment'}</button>
        </div>
      </form>
    </div>
  );
};

export default PaymentModal;
